import React, { useCallback, useEffect, useRef, useState } from 'react'
import useInterval from '../../util/useInterval'
// borrowed and updated from react-countdown-clock, which appears inactive and no longer works with latest react (or typescript)

type Props = {
  seconds: number
  size?: number
  weight?: number
  color: string
  fontSize?: string
  font?: string
  alpha: number
  onComplete: () => void
  isStartPaused?: boolean
}
const CountdownClock = (props: Props) => {
  const {
    seconds = 60,
    size = 300,
    weight = 0,
    color = '#000',
    alpha = 1,
    fontSize = 'auto',
    font = 'Arial',
    onComplete,
    isStartPaused = false,
  } = props
  const [lastRestartDuration, setLastRestartDuration] = useState<number>(seconds)
  const [secondsRemaining, setSecondsRemaining] = useState<number>(seconds)
  const [isPaused, setIsPaused] = useState<boolean>(true)
  const [startTime, setStartTime] = useState<number>(0)
  const canvasStyle: React.CSSProperties = {
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
  }
  const backgroundRef = useRef<HTMLCanvasElement>(null)
  const timerRef = useRef<HTMLCanvasElement>(null)
  const fraction = 2 / seconds
  const radius = size / 2
  const innerRadius = weight ? radius - weight : radius / 1.8
  const calculateTick = () => {
    // Tick period needs to be fast for smaller time periods and slower
    // for longer ones. This provides smoother rendering. It should never exceed 1 second.
    const tickScale = 1.8
    const tick = seconds * tickScale
    return tick > 1000 ? 1000 : tick
  }

  const tickPeriod = calculateTick()
  const [timer, setTimer] = useState<CanvasRenderingContext2D | null>(null)
  const [background, setBackground] = useState<CanvasRenderingContext2D | null>(null)
  const canvasProps = {
    style: canvasStyle,
    height: size,
    width: size,
  }

  useEffect(
    function setupCanvases() {
      if (!backgroundRef.current || !timerRef.current) {
        return
      }
      const initialBackgroundCanvas = backgroundRef.current.getContext('2d')
      const initialTimerCanvas = timerRef.current.getContext('2d')
      if (!initialTimerCanvas || !initialBackgroundCanvas) {
        throw new Error('could not get background/timer canvases')
      }
      initialTimerCanvas.textAlign = 'center'
      initialTimerCanvas.textBaseline = 'middle'
      setBackground(initialBackgroundCanvas)
      setTimer(initialTimerCanvas)
    },
    [backgroundRef, timerRef]
  )

  useEffect(
    function drawTimer() {
      if (!timer || !timerRef.current) {
        return
      }
      const getFontSize = (timeString: string) => {
        if (fontSize === 'auto') {
          const length = timeString.length
          const scale =
            length === 8
              ? 4 // hh:mm:ss
              : length === 5
              ? 3 // mm:ss
              : 2 // ss or ss.s
          const size = radius / scale
          return `${size}px`
        }
        return fontSize
      }
      const formatTime = () => {
        const hours = Math.floor(secondsRemaining / 3600) % 24
        const minutes = Math.floor(secondsRemaining / 60) % 60
        const timeSeconds = Math.ceil(secondsRemaining) % 60

        const hoursStr = `${hours < 10 ? '0' : ''}${hours}`
        const minutesStr = `${minutes < 10 && hours >= 1 ? '0' : ''}${minutes}`
        const secondsStr = `${
          timeSeconds < 10 && (minutes >= 1 || hours >= 1) ? '0' : ''
        }${timeSeconds}`

        const timeParts: string[] = []
        if (hours > 0) timeParts.push(hoursStr)
        if (minutes > 0 || hours > 0) timeParts.push(minutesStr)
        timeParts.push(secondsStr)

        return timeParts.join(':')
      }
      const percent = fraction * secondsRemaining + 1.5
      const formattedTime = formatTime()
      timer.clearRect(0, 0, timerRef.current.width, timerRef.current.height)
      timer.globalAlpha = alpha
      timer.fillStyle = color
      timer.font = `bold ${getFontSize(formattedTime)} ${font}`
      timer.fillText(formattedTime, radius, radius)
      timer.beginPath()
      timer.arc(radius, radius, radius, Math.PI * 1.5, Math.PI * percent, false)
      timer.arc(radius, radius, innerRadius, Math.PI * percent, Math.PI * 1.5, true)
      timer.closePath()
      timer.fill()
    },
    [timer, timerRef, secondsRemaining, alpha, color, font, fontSize, fraction, innerRadius, radius]
  )
  const tick = useCallback(() => {
    const now = Date.now()
    const secondsElapsed = (now - startTime) / 1000
    const newSecondsRemaining = Math.max(0, lastRestartDuration - secondsElapsed)
    // console.log(
    //   `duration: ${seconds}, now: ${now}, startTime: ${startTime}, elapsed: ${secondsElapsed}, remaining: ${newSecondsRemaining}`,
    // )
    setSecondsRemaining(newSecondsRemaining)
    if (!newSecondsRemaining) {
      setIsPaused(true)
      if (onComplete) {
        onComplete()
      }
    }
  }, [onComplete, lastRestartDuration, startTime])
  useInterval(tick, isPaused ? null : tickPeriod)

  const toggleTimer = useCallback((isStart: boolean) => {
    if (isStart) {
      setStartTime(Date.now())
      setIsPaused(false)
    } else {
      setIsPaused(true)
    }
  }, [])
  useEffect(() => {
    if (!isStartPaused) {
      toggleTimer(true)
    }
  }, [isStartPaused, toggleTimer])
  useEffect(
    function setUpTimer() {
      if (!background || !timer || !timerRef) {
        return
      }
      const drawBackground = () => {
        const { width, height } = timerRef.current || { width: 0, height: 0 }
        background.clearRect(0, 0, width, height)
        background.beginPath()
        background.globalAlpha = alpha / 3
        background.fillStyle = color
        background.arc(radius, radius, radius, 0, Math.PI * 2, false)
        background.arc(radius, radius, innerRadius, Math.PI * 2, 0, true)
        background.closePath()
        background.fill()
      }
      drawBackground()
    },
    [background, timer, timerRef, alpha, color, radius, innerRadius]
  )
  const onClick = () => {
    toggleTimer(isPaused)
    setLastRestartDuration(secondsRemaining)
  }
  return (
    <div className="countdownClock" style={{ width: size, height: size }} onClick={onClick}>
      <canvas ref={backgroundRef} {...canvasProps} />
      <canvas ref={timerRef} {...canvasProps}></canvas>
    </div>
  )
}

export default CountdownClock
